/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect } from 'react'

import { PascodeMetadata } from '../../assets/ts/data/pascode-metadata'
import { getMAJCOMCode, getMAJCOMString } from '../../assets/ts/general/general'
import { isMatch } from '../../assets/ts/validations/validations'
import { MetadataContext } from '../../store/metadata-context-provider'
import { MAX_LENGTHS } from '../../utils/constants/global-constants'
import InputCheckbox from '../../utils/controls/input-checkbox'
import InputText from '../../utils/controls/input-text'

interface MatchError {
  Installation: boolean
  Organization: boolean
  PASCODE: boolean
}

const ProfileOrganizationDetails = ({
  assignedLocationDDL,
  existingUser,
  inputRefs,
  inputValidationObj,
  inquiryForSelf = false,
  isProfilePage = false,
  isRegAF,
  matchError,
  organizationDDL,
  organizationPASCODE,
  pascodeChecks,
  pascodeDDL,
  pascodeMetadata,
  setMatchError,
  setOrganizationDDL,
  setPascodeChecks,
  setShowReadonlyInput,
  setUserInputs,
  showReadonlyInput,
  userInputs,
}: {
  assignedLocationDDL: string[]
  existingUser: any
  inputRefs: any
  inputValidationObj: any
  inquiryForSelf: boolean
  isProfilePage?: boolean
  isRegAF: boolean
  matchError: MatchError
  organizationDDL: string[]
  organizationPASCODE: PascodeMetadata[]
  pascodeChecks: any
  pascodeDDL: string[]
  pascodeMetadata: any[]
  setMatchError: any
  setOrganizationDDL: any
  setPascodeChecks: any
  setShowReadonlyInput: any
  setUserInputs: any
  showReadonlyInput: boolean
  userInputs: any
}) => {
  const { majcoms, tooltipObj } = useContext(MetadataContext)

  const knowMyPASCODE = (e: { target: { value: string } }) => {
    const { value } = e.target
    if (value === 'Yes' && existingUser.PASCODE && inquiryForSelf && existingUser.AssignedComponentCode === userInputs.AssignedComponentCode) {
      setUserInputs({
        ...userInputs,
        Installation: existingUser.Installation,
        OrgMAJCOM: existingUser.OrgMAJCOM,
        Organization: existingUser.Organization,
        PASCODE: existingUser.PASCODE,
        PASCODEServicedBy: existingUser.PASCODEServicedBy,
        PascodeID: existingUser.PascodeID,
        ServicingMAJCOM: existingUser.ServicingMAJCOM,
        ServicingMAJCOMCode: getMAJCOMCode(existingUser.ServicingMAJCOM, majcoms),
        ServicingOrganization: existingUser.ServicingOrganization,
      })
      setShowReadonlyInput(true)
      setPascodeChecks({
        HasLocation: true,
        HasOrganization: true,
        HasPASCODE: 'Yes',
      })
    } else {
      setShowReadonlyInput(false)
      setUserInputs({
        ...userInputs,
        Installation: '',
        OrgMAJCOM: '',
        Organization: '',
        PASCODE: '',
        PASCODEServicedBy: '',
        PascodeID: -1,
        ServicingMAJCOM: '',
        ServicingMAJCOMCode: -1,
        ServicingOrganization: '',
      })
      setPascodeChecks({
        ...pascodeChecks,
        HasLocation: false,
        HasOrganization: false,
        HasPASCODE: value,
      })
      matchError !== undefined &&
        setMatchError({
          Installation: true,
          Organization: true,
          PASCODE: true,
        })
    }
  }

  const changePASCODE = (e: { target: { value: string } }) => {
    const { value } = e.target
    const regex = /^[a-zA-Z0-9]*$/
    const valueIsOnlyNumsOrChars = regex.test(value)
    setShowReadonlyInput(false)

    // valueIsOnlyNumsOrChars is in a separate if statement to prevent the else statement from clearing everything out, in the event that valueIsOnlyNumsOrChars is false.
    if (valueIsOnlyNumsOrChars) {
      if (value.length > 2 && !isMatch(pascodeDDL, value)) {
        matchError !== undefined && setMatchError({ ...matchError, PASCODE: false })
      } else {
        matchError !== undefined && setMatchError({ ...matchError, PASCODE: true })
      }
      setShowReadonlyInput(false)
      setUserInputs((prevUserInput) => ({
        ...prevUserInput,
        Installation: '',
        OrgMAJCOM: '',
        Organization: '',
        PASCODE: value,
        PASCODEServicedBy: '',
        PascodeID: -1,
        ServicingMAJCOM: '',
        ServicingMAJCOMCode: -1,
        ServicingOrganization: '',
      }))
      setPascodeChecks((prevPascodeChecks) => ({
        ...prevPascodeChecks,
        HasLocation: false,
        HasOrganization: false,
      }))
    }
  }
  const selectPASCODE = (value: string) => {
    const {
      AssignedComponentCode,
      Code,
      Installation,
      OrgMAJCOM,
      Organization,
      ServicingBy,
      ServicingMAJCOM,
      ServicingOrganization,
    } = findOrganizationDetails(pascodeMetadata, value, 'PASCODE')

    if (isInputValid(value)) {
      setShowReadonlyInput(true)
      setUserInputs((prevUserInput) => ({
        ...prevUserInput,
        AssignedComponentCode: AssignedComponentCode,
        Installation: Installation,
        OrgMAJCOM: OrgMAJCOM,
        Organization: Organization,
        PASCODE: value,
        PASCODEServicedBy: ServicingBy,
        PascodeID: Code,
        ServicingMAJCOM: ServicingMAJCOM,
        ServicingMAJCOMCode: getMAJCOMCode(ServicingMAJCOM, majcoms),
        ServicingOrganization: ServicingOrganization,
      }))
      setPascodeChecks((prevPascodeChecks) => ({
        ...prevPascodeChecks,
        HasLocation: true,
        HasOrganization: true,
      }))
    } else {
      setUserInputs((prevUserInput) => ({
        ...prevUserInput,
        Installation: '',
        Organization: '',
        PASCODE: '',
        PASCODEServicedBy: '',
        ServicingOrganization: '',
      }))
      setPascodeChecks((prevPascodeChecks) => ({
        ...prevPascodeChecks,
        HasLocation: false,
        HasOrganization: false,
      }))
    }
  }

  const findOrganizationDetails = (list, name, filterBy) => {
    const filterFunc = (item) => {
      if (filterBy === 'PASCODE') return item.PASCODE === name
      if (filterBy === 'ORGANIZATION') return item.Organization === name
      return false
    }

    return list.find(filterFunc) || {}
  }

  const changeLocation = (e: { target: { value: string } }) => {
    const { value } = e.target
    if (isInputValid(value)) {
      if (value.length > 2 && !isMatch(assignedLocationDDL, value)) {
        matchError !== undefined && setMatchError({ ...matchError, Installation: false })
      } else {
        matchError !== undefined && setMatchError({ ...matchError, Installation: true })
      }
      setUserInputs((prevUserInput) => ({
        ...prevUserInput,
        Installation: value,
        Organization: '',
        PASCODE: '',
        PASCODEServicedBy: '',
        PascodeID: -1,
        ServicingMAJCOM: '',
        ServicingMAJCOMCode: -1,
        ServicingOrganization: '',
      }))
      setPascodeChecks((prevPascodeChecks) => ({
        ...prevPascodeChecks,
        HasLocation: false,
      }))
      setShowReadonlyInput(false)
    } else {
      setUserInputs((prevUserInput) => ({
        ...prevUserInput,
        Installation: '',
        Organization: '',
        PASCODE: '',
        PASCODEServicedBy: '',
        PascodeID: -1,
        ServicingOrganization: '',
      }))
      setPascodeChecks((prevPascodeChecks) => ({
        ...prevPascodeChecks,
        HasLocation: false,
      }))
    }
  }

  const selectLocation = (value) => {
    const organizations = [
      ...new Set(
        organizationPASCODE?.map((item) => {
          if (item.Installation === value) {
            return item.Organization
          }
        })
      ),
    ].filter((item) => item !== undefined)
    setOrganizationDDL(organizations)
    setUserInputs((prevUserInput) => ({
      ...prevUserInput,
      AssignedComponentCode: -1,
      Installation: value,
      Organization: '',
      PASCODE: '',
      PASCODEServicedBy: '',
      PascodeID: -1,
      ServicingMAJCOM: '',
      ServicingMAJCOMCode: -1,
      ServicingOrganization: '',
    }))
    setPascodeChecks((prevPascodeChecks) => ({
      ...prevPascodeChecks,
      HasLocation: true,
    }))
  }

  const changeOrganization = (e: { target: { value: string } }) => {
    const { value } = e.target
    if (isInputValid(value)) {
      if (value.length > 2 && !isMatch(organizationDDL, value)) {
        matchError !== undefined && setMatchError({ ...matchError, Organization: false })
      } else {
        matchError !== undefined && setMatchError({ ...matchError, Organization: true })
      }
      setPascodeChecks({
        ...pascodeChecks,
        HasOrganization: false,
      })
      setShowReadonlyInput(false)
      setUserInputs((prevUserInput) => ({
        ...prevUserInput,
        OrgMAJCOM: '',
        Organization: value,
        PASCODE: '',
        PASCODEServicedBy: '',
        PascodeID: -1,
        ServicingMAJCOM: '',
        ServicingMAJCOMCode: -1,
        ServicingOrganization: '',
      }))
    } else {
      setUserInputs((prevUserInput) => ({
        ...prevUserInput,
        Organization: '',
        PASCODE: '',
        PASCODEServicedBy: '',
        PascodeID: -1,
        ServicingOrganization: '',
      }))
      setPascodeChecks((prevPascodeChecks) => ({
        ...prevPascodeChecks,
        HasOrganization: false,
        HasPASCODE: 'No',
      }))
    }
  }
  const selectOrganization = (value) => {
    const {
      AssignedComponentCode,
      Code,
      OrgMAJCOM,
      PASCODE,
      ServicingBy,
      ServicingMAJCOM,
      ServicingOrganization,
    } = findOrganizationDetails(organizationPASCODE, value, 'ORGANIZATION')
    setPascodeChecks({
      ...pascodeChecks,
      HasOrganization: true,
    })
    setShowReadonlyInput(true)
    setUserInputs({
      ...userInputs,
      AssignedComponentCode: AssignedComponentCode,
      OrgMAJCOM: OrgMAJCOM,
      Organization: value,
      PASCODE: PASCODE,
      PASCODEServicedBy: ServicingBy,
      PascodeID: Code,
      ServicingMAJCOM: ServicingMAJCOM,
      ServicingMAJCOMCode: getMAJCOMCode(ServicingMAJCOM, majcoms),
      ServicingOrganization: ServicingOrganization,
    })
  }

  function isInputValid(input) {
    let valid = false
    if (input === '' || input === null || input === 'undefined' || checkForOnlySpaces(input)) {
      valid = false
    } else if (input.length > 0) {
      valid = true
    }
    return valid
  }
  const checkForOnlySpaces = (val: string) => /^\s*$/.test(val)

  //updates Servicing MAJCOM string from code
    useEffect(() => {
      if (userInputs?.ServicingMAJCOM && majcoms) {
        setUserInputs({...userInputs, 
          ServicingMAJCOMCode: getMAJCOMCode(userInputs.ServicingMAJCOM, majcoms),

        })
      }
    }, [majcoms, userInputs.ServicingMAJCOM])

  return (
    <>
      {isProfilePage && (
        <div className='p-2 bgcolor-21 w-100'>
          <h3
            aria-label='Organizational Details '
            className='text-uppercase title-color51 poppins-semibold font-12 d-inline-block m-0 py-1 px-2'
            tabIndex={0}
          >
            Organizational Details
          </h3>
        </div>
      )}
      {!isProfilePage && (
        <div className='mt-4 col-xl-12 col-lg-12 col-md-6 col-sm-12 d-flex align-items-center'>
          <div className='d-flex me-3'>
            <label
              className='font-12 poppins-medium title-color form-horztional'
              htmlFor='Do you know the PASCODE?'
            >
              Do You Know The Unit PASCODE? <span className='mandatory me-1'>*</span>
              <span
                className='tool-tip tooltip-bottom font-12 sourcesanspro'
                data-tip='Do You Know The Unit PASCODE?'
              >
                <span className='icon-info color-primary'></span>
              </span>
            </label>
          </div>
          <div className='d-flex align-items-center'>
            <InputCheckbox
              formClassName='d-flex align-items-center'
              inputProps={{
                arialabel: 'Yes',
                checked: pascodeChecks.HasPASCODE === 'Yes',
                className: 'font-12 poppins-medium title-color mx-2',
                id: 'YesRadio',
                label: 'Yes',
                name: 'Pascoderadio',
                type: 'radio',
                value: 'Yes',
              }}
              onChange={knowMyPASCODE}
            />
            <InputCheckbox
              formClassName='d-flex align-items-center'
              inputProps={{
                arialabel: 'No',
                checked: pascodeChecks.HasPASCODE === 'No',
                className: 'font-12 poppins-medium title-color mx-2',
                id: 'NoRadio',
                label: 'No',
                name: 'Pascoderadio',
                type: 'radio',
                value: 'No',
              }}
              onChange={knowMyPASCODE}
            />
          </div>
        </div>
      )}
      <div className={!isProfilePage ? 'py-2' : 'px-3 py-2'}>
        <div className=' row align-items-start'>
          {isProfilePage && (
            <div className='mb-1 form-vertical mt-2  col-12 col-sm-6 col-lg-4 col-xxl-3 d-flex flex-column align-items-start justify-content-between pascodeinfo'>
              <div>
                <label
                  className='font-12 poppins-medium title-color form-horztional'
                  htmlFor='Do you know the PASCODE?'
                >
                  Do You Know The Unit PASCODE?
                  <span
                    className='tool-tip tooltip-bottom font-12 sourcesanspro ps-1'
                    data-tip='Do You Know The Unit PASCODE?'
                  >
                    <span className='icon-info color-primary'></span>
                  </span>
                </label>
              </div>
              <div className='d-flex align-items-center'>
                <InputCheckbox
                  formClassName='d-flex align-items-center'
                  inputProps={{
                    arialabel: 'Yes',
                    checked: pascodeChecks.HasPASCODE === 'Yes',
                    className: 'font-12 poppins-medium title-color mx-2',
                    id: 'YesRadio',
                    label: 'Yes',
                    name: 'Pascoderadio',
                    type: 'radio',
                    value: 'Yes',
                  }}
                  onChange={knowMyPASCODE}
                />
                <InputCheckbox
                  formClassName='d-flex align-items-center'
                  inputProps={{
                    arialabel: 'No',
                    checked: pascodeChecks.HasPASCODE === 'No',
                    className: 'font-12 poppins-medium title-color mx-2',
                    id: 'NoRadio',
                    label: 'No',
                    name: 'Pascoderadio',
                    type: 'radio',
                    value: 'No',
                  }}
                  onChange={knowMyPASCODE}
                />
              </div>
            </div>
          )}
          {pascodeChecks.HasPASCODE === 'Yes' && (
            <>
              {showReadonlyInput && (
                <div
                  className={
                    !isProfilePage
                      ? 'mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'
                      : 'mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'
                  }
                >
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical'
                    info={tooltipObj.installation?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'AssignedLocation?',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'What is your Assigned Location?',
                      placeholder: 'Assigned Location?',
                      value: userInputs.Installation,
                    }}
                    isInfo
                    isMandatory
                    label='Installation/Assigned Location'
                    ref={inputValidationObj?.Installation?.link}
                  />
                </div>
              )}

              {showReadonlyInput && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical'
                    info={tooltipObj.organization?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'Organization?',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'What is your Organization',
                      placeholder: 'Test Org MPF',
                      value: userInputs.Organization,
                    }}
                    isInfo
                    isMandatory
                    label='Organization/Unit Name'
                    ref={inputValidationObj?.Organization?.link}
                  />
                </div>
              )}

              <div
                className={
                  !isProfilePage
                    ? 'mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'
                    : 'mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'
                }
              >
                <InputText
                  changeHandler={changePASCODE}
                  className='form-lg font-13 sourcesanspro'
                  data={pascodeDDL}
                  error={matchError !== undefined && !matchError?.PASCODE}
                  formClassName=' form-vertical'
                  info={tooltipObj.profilePASCODE?.TooltipDescription}
                  infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                  infoIcon='icon-info color-primary'
                  inputProps={{
                    className: 'font-12 poppins-medium title-color',
                    id: 'PASCODE',
                    maxLength: MAX_LENGTHS.EIGHT,
                    name: 'PASCODE',
                    placeholder: 'PASCODE',
                  }}
                  isInfo
                  isMandatory
                  label='PASCODE'
                  maxLength={MAX_LENGTHS.EIGHT}
                  onSelect={selectPASCODE}
                  ref={!isProfilePage ? inputValidationObj.PASCODE?.link : inputRefs?.PASCODE}
                  value={userInputs.PASCODE}
                />
              </div>
              {showReadonlyInput && isRegAF && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical '
                    info={tooltipObj.servicingBy?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'ServicedBy',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'PASCODE Serviced By',
                      value: userInputs.PASCODEServicedBy,
                    }}
                    isInfo
                    label='PASCODE Serviced By'
                  />
                </div>
              )}

              {showReadonlyInput && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical'
                    info={tooltipObj.servicingOrg?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'ServicingOrganization',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'Servicing Organization',
                      value: userInputs.ServicingOrganization,
                    }}
                    isInfo
                    label='Servicing Organization'
                  />
                </div>
              )}

              {showReadonlyInput && isRegAF && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical'
                    info={tooltipObj.profileServicingMAJCOM?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'ServicingMAJCOM',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'Servicing MAJCOM',
                      value: getMAJCOMString(userInputs?.ServicingMAJCOMCode, majcoms),
                    }}
                    isInfo
                    label='Servicing MAJCOM'
                  />
                </div>
              )}
            </>
          )}

          {pascodeChecks.HasPASCODE === 'No' && (
            <>
              <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-20per mt-2'>
                <InputText
                  changeHandler={changeLocation}
                  className='form-lg font-13 sourcesanspro'
                  data={assignedLocationDDL}
                  error={matchError !== undefined && !matchError?.Installation}
                  formClassName=' form-vertical'
                  info={tooltipObj.installation?.TooltipDescription}
                  infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                  infoIcon='icon-info color-primary'
                  inputProps={{
                    className: 'font-12 poppins-medium title-color',
                    id: 'AssignedLocation?',
                    maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                    name: 'What is your Assigned Location?',
                    placeholder: 'Assigned Location',
                  }}
                  isInfo
                  isMandatory
                  label='Installation/Assigned Location'
                  onSelect={selectLocation}
                  ref={inputRefs?.Installation || inputValidationObj?.Installation.link}
                  value={userInputs.Installation}
                />
              </div>
              {pascodeChecks.HasPASCODE === 'No' && pascodeChecks.HasLocation && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    changeHandler={changeOrganization}
                    className='form-lg font-13 sourcesanspro'
                    data={organizationDDL}
                    error={matchError !== undefined && !matchError.Organization}
                    formClassName=' form-vertical'
                    info={tooltipObj.organization?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'Organization?',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'What is your Organization',
                      placeholder: 'Organization',
                    }}
                    isInfo
                    isMandatory
                    label='Organization/Unit Name'
                    onSelect={selectOrganization}
                    ref={inputRefs?.Organization || inputValidationObj?.Organization.link}
                    value={userInputs.Organization}
                  />
                </div>
              )}
              {showReadonlyInput && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical'
                    info={tooltipObj.profilePASCODE?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'PASCODE',
                      maxLength: MAX_LENGTHS.EIGHT,
                      name: 'PASCODE',
                      value: userInputs.PASCODE,
                    }}
                    isInfo
                    isMandatory
                    label='PASCODE'
                    ref={inputValidationObj?.PASCODE?.link}
                  />
                </div>
              )}

              {showReadonlyInput && isRegAF && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical '
                    info={tooltipObj.servicingBy?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'ServicedBy',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'PASCODE Serviced By',
                      value: userInputs.PASCODEServicedBy,
                    }}
                    isInfo
                    label='PASCODE Serviced By'
                  />
                </div>
              )}

              {showReadonlyInput && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical'
                    info={tooltipObj.servicingOrg?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'ServicingOrganization',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'Servicing Organization',
                      value: userInputs.ServicingOrganization,
                    }}
                    isInfo
                    label='Servicing Organization'
                  />
                </div>
              )}

              {showReadonlyInput && isRegAF && (
                <div className='mb-1 col-12 col-sm-6 col-lg-4 col-xxl-3 mt-2'>
                  <InputText
                    className='form-lg font-13 sourcesanspro'
                    disabled
                    formClassName=' form-vertical'
                    info={tooltipObj.profileServicingMAJCOM?.TooltipDescription}
                    infoClassName='tool-tip tooltip-top font-12 sourcesanspro'
                    infoIcon='icon-info color-primary'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'ServicingMAJCOM',
                      maxLength: MAX_LENGTHS.TWO_HUNDRED_FIFTY,
                      name: 'Servicing MAJCOM',
                      value:  getMAJCOMString(userInputs?.ServicingMAJCOMCode, majcoms),
                    }}
                    isInfo
                    label='Servicing MAJCOM'
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
export default ProfileOrganizationDetails
